import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API } from "@/services/API";
import { validateTrailerType } from "@/utils/validation";
import { lockedLoadVolume } from "@/utils/lockedData";

const initialState = {
  data: [],
  loading: false,
  error: null,
};

export const getLoadVolume = createAsyncThunk(
  "search/get-load-volume",
  async ({ trailerType, pickupZip, deliveryZip, loadVolumeInterval }, { rejectWithValue, getState }) => {
    const isLocked = !(getState().user.data?.featuresUnlocked ?? false);

    if (isLocked) {
      return lockedLoadVolume;
    }

    if (
      !trailerType ||
      !pickupZip ||
      !validateTrailerType(trailerType) ||
      isNaN(pickupZip) ||
      (deliveryZip !== "" && isNaN(deliveryZip)) ||
      !(loadVolumeInterval === 90 || loadVolumeInterval === 365)
    ) {
      const errorMessage = "Invalid load volume parameters provided";
      console.error("Error fetching load volume data", errorMessage);
      return rejectWithValue(errorMessage);
    }

    return API.get("/analytics/zip2zip-weekly", {
      params: {
        departure: pickupZip,
        "drop-off": deliveryZip,
        trailer: trailerType,
        "interval-in-days": loadVolumeInterval,
      },
    })
      .then((res) => {
        return res.volumePerDayOfTheWeek.map((v) => parseInt(v * 100));
      })
      .catch((err) => {
        console.error("Error fetching load volume data", err);
        return rejectWithValue(err);
      });
  },
);

const loadVolumeSlice = createSlice({
  name: "loadVolume",
  initialState,
  reducers: {
    clearLoadVolumeState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLoadVolume.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLoadVolume.fulfilled, (state, action) => ({
        data: action.payload,
        loading: false,
        error: null,
      }))
      .addCase(getLoadVolume.rejected, () => ({
        data: [],
        loading: false,
        error: "Error getting load volume",
      }));
  },
});

export const { clearLoadVolumeState } = loadVolumeSlice.actions;
export default loadVolumeSlice.reducer;
